import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import OnboardingModal from "@/components/OnboardingModal";

// Eager: Critical path (homepage loads instantly)
import HomePage from "@/pages/HomePage";

// Lazy: All other pages (loaded on demand)
const MarathonTablePage = lazy(() => import("@/pages/MarathonTablePage"));
const TeamDetailPage = lazy(() => import("@/pages/TeamDetailPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const SeasonPage = lazy(() => import("@/pages/SeasonPage"));
const AdvertisePage = lazy(() => import("@/pages/AdvertisePage"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const AuthorPage = lazy(() => import("@/pages/AuthorPage"));
const WriterDashboardPage = lazy(() => import("@/pages/WriterDashboardPage"));
const NewsPage = lazy(() => import("@/pages/NewsPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const TopScorersPage = lazy(() => import("@/pages/TopScorersPage"));
const AssistLeaguePage = lazy(() => import("@/pages/AssistLeaguePage"));
const CardLeaguePage = lazy(() => import("@/pages/CardLeaguePage"));
const SupporterIndexPage = lazy(() => import("@/pages/SupporterIndexPage"));
const MatchSchedulePage = lazy(() => import("@/pages/MatchSchedulePage"));
const MatchDetailPage = lazy(() => import("@/pages/MatchDetailPage"));
const PlayerProfilePage = lazy(() => import("@/pages/PlayerProfilePage"));
const LiveScoresPage = lazy(() => import("@/pages/LiveScoresPage"));
const AIContentPage = lazy(() => import("@/pages/AIContentPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const CountyRankingPage = lazy(() => import("@/pages/CountyRankingPage"));

// Minimal loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-8 h-8 border-3 border-gray-200 border-t-[#0F172A] rounded-full animate-spin" />
    </div>
  );
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// App router with session_id check
function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }
  
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/tabell" element={<MarathonTablePage />} />
            <Route path="/maratontabell" element={<MarathonTablePage />} />
            <Route path="/jamfor" element={<ComparePage />} />
            <Route path="/skytteliga" element={<TopScorersPage />} />
            <Route path="/assistliga" element={<AssistLeaguePage />} />
            <Route path="/kortliga" element={<CardLeaguePage />} />
            <Route path="/matcher" element={<MatchSchedulePage />} />
            <Route path="/matcher/:fixtureId" element={<MatchDetailPage />} />
            <Route path="/live" element={<LiveScoresPage />} />
            <Route path="/ai-analys" element={<AIContentPage />} />
            <Route path="/spelare/:playerId" element={<PlayerProfilePage />} />
            <Route path="/nyheter" element={<NewsPage />} />
            <Route path="/blogg" element={<BlogPage />} />
            <Route path="/blogg/forfattare/:authorId" element={<AuthorPage />} />
            <Route path="/blogg/:postId" element={<BlogPostPage />} />
            <Route path="/skriv" element={<WriterDashboardPage />} />
            <Route path="/sasong/:season" element={<SeasonPage />} />
            <Route path="/lag/:teamId" element={<TeamDetailPage />} />
            <Route path="/lan/:countySlug" element={<CountyRankingPage />} />
            <Route path="/om" element={<AboutPage />} />
            <Route path="/annonsera" element={<AdvertisePage />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/admin/ads" element={<AdminPanel />} />
            <Route path="/admin/import" element={<AdminPanel />} />
            <Route path="/admin/blog" element={<AdminPanel />} />
            <Route path="/admin/authors" element={<AdminPanel />} />
            <Route path="/admin/users" element={<AdminPanel />} />
            <Route path="/kontakt" element={<ContactPage />} />
            <Route path="/integritetspolicy" element={<PrivacyPolicyPage />} />
            <Route path="/logga-in" element={<LoginPage />} />
            <Route path="/registrera" element={<RegisterPage />} />
            <Route path="/profil" element={<ProfilePage />} />
            <Route path="/supporterindex" element={<SupporterIndexPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieConsent />
      <OnboardingModal />
    </>
  );
}

function App() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
